// Suport RO/EN pentru TSRA — pattern comun folosit în restul platformei
// CentralSec (Hub Client, Investigații etc.): stare `lang` persistată în
// localStorage, un dicționar RO→EN și un helper `t(lang, roText)` care
// întoarce EN doar dacă textul RO există ca cheie în dicționar — altfel
// întoarce mereu RO nemodificat (niciodată o cheie lipsă/brută vizibilă).
//
// Sursa de adevăr a textului rămâne română (în JSX/scales.js), exact ca la
// Investigații — dicționarul de mai jos e doar traducerea, nu duplică
// conținutul.

import { createContext, useContext, useEffect, useState } from "react";

export const LANG_KEY = "tsra-lang";

// ──────────────────────────────────────────────────────────────────────
// Dicționar RO → EN
// ──────────────────────────────────────────────────────────────────────
export const EN_STRINGS = {
  // ── Chrome / topbar / login ──────────────────────────────────────────
  "Ghid metodologie": "Methodology guide",
  "Ieșire": "Logout",
  "Securitate cont": "Account Security",
  "Notificări": "Notifications",
  "Nicio notificare încă.": "No notifications yet.",
  "Cont": "Account",
  "Rol": "Role",
  "Se conectează…": "Connecting…",
  "Conectare": "Login",
  "Parolă": "Password",
  "Se încarcă…": "Loading…",
  "Anulează": "Cancel",
  "Salvează": "Save",
  "Șterge": "Delete",
  "Creează": "Create",
  "OK": "OK",

  // ── Clienți ───────────────────────────────────────────────────────────
  "Clienți": "Clients",
  "+ Client nou": "+ New client",
  "Denumire": "Name",
  "Domeniu (opțional)": "Domain (optional)",
  "Niciun client încă.": "No clients yet.",

  // ── Evaluări (per client) ────────────────────────────────────────────
  "← Clienți": "← Clients",
  "Evaluări": "Assessments",
  "+ Evaluare nouă": "+ New assessment",
  "Titlu": "Title",
  "Evaluator": "Assessor",
  "Nicio evaluare încă.": "No assessments yet.",
  "Evaluare inițială 2026": "Initial assessment 2026",

  // ── Stare evaluare (enum backend Ciorna/Finalizata) ─────────────────
  "Ciornă": "Draft",
  "Finalizată": "Finalized",

  // ── Evaluare detail — shell ──────────────────────────────────────────
  "← Evaluări": "← Assessments",
  "Populează implicit": "Populate defaults",
  "Adaugă un set implicit de Amenințări, Hazarduri și Controale ca punct de plecare":
    "Adds a default set of Threats, Hazards, and Controls as a starting point",
  "Următoarea revizuire": "Next review",
  "restantă": "overdue",
  "Fără dată de revizuire": "No review date",
  "Click pentru a modifica": "Click to edit",

  // ── Tab-uri ───────────────────────────────────────────────────────────
  "Raport": "Report",
  "Active": "Assets",
  "Amenințări": "Threats",
  "Hazarduri": "Hazards",
  "Registru de risc": "Risk register",
  "Tratamente": "Treatments",
  "Controale": "Controls",
  "Cadrane de amenințare": "Threat quadrants",
  "Plan de lucru": "Work plan",

  // ── Catalog picker ────────────────────────────────────────────────────
  "Alege din catalog (opțional)": "Choose from catalog (optional)",
  "— selectează pentru a precompleta din xlsx —": "— select to prefill from the xlsx —",

  // ── Active (tab) ─────────────────────────────────────────────────────
  "+ Activ nou": "+ New asset",
  "Categorie": "Category",
  "Criticitate (1–5 fiecare)": "Criticality (1–5 each)",
  "Recuperabilitate": "Recoverability",
  "Calități temporale": "Temporal qualities",
  "Dependență": "Dependency",
  "Oportunitate (1–5 fiecare)": "Opportunity (1–5 each)",
  "Atractivitate": "Attractiveness",
  "Expunere": "Exposure",
  "Accesibilitate": "Accessibility",
  "Comentarii": "Comments",
  "Cod": "Code",
  "Activ": "Asset",
  "Criticitate": "Criticality",
  "Oportunitate": "Opportunity",

  // ── Amenințări (tab) ─────────────────────────────────────────────────
  "+ Amenințare nouă": "+ New threat",
  "Capabilitate (1–5 fiecare)": "Capability (1–5 each)",
  "Resurse": "Resources",
  "Cunoaștere": "Knowledge",
  "Intenție (1–5 fiecare)": "Intent (1–5 each)",
  "Dorință": "Desire",
  "Încredere": "Confidence",
  "Descriere": "Description",
  "Amenințare": "Threat",
  "Capabilitate": "Capability",
  "Intenție": "Intent",
  "Indice amenințare": "Threat index",

  // ── Hazarduri (tab) ──────────────────────────────────────────────────
  "+ Hazard nou": "+ New hazard",
  // Notă: "Adecvare" apare atât ca scală Hazard, cât și ca scor Control —
  // aceeași traducere ("Adequacy") folosită peste tot, ca t() (cheie unică
  // pe text RO) să nu aibă nevoie de context pentru dezambiguizare.
  "Adecvare": "Adequacy",
  "Disponibilitate": "Availability",
  "Implementare": "Implementation",
  "Hazard": "Hazard",
  "Eficacitate": "Effectiveness",

  // ── Registru de risc (tab) ───────────────────────────────────────────
  "Nedecis": "Undecided",
  "Acceptat": "Accepted",
  "Respins": "Rejected",
  "Portofoliu de risc": "Risk portfolio",
  "risc": "risk",
  "riscuri": "risks",
  "Consecință": "Consequence",
  "Probabilitate": "Probability",
  "Adaugă cel puțin un Activ, o Amenințare și un Hazard înainte de a construi registrul de risc.":
    "Add at least one Asset, Threat, and Hazard before building the risk register.",
  "+ Rând de risc nou": "+ New risk row",
  "Categorie risc (opțional, implicit din categoria Activului)":
    "Risk category (optional, defaults from the Asset's category)",
  "Descriere risc (opțional, implicit generată automat)":
    "Risk description (optional, auto-generated by default)",
  "Risc inerent": "Inherent risk",
  "Bandă": "Band",
  "Eficacitate controale": "Control effectiveness",
  "Risc curent": "Current risk",
  "Risc rezidual": "Residual risk",
  "Decizie": "Decision",
  "Setează": "Set",
  "Justificare": "Justification",
  "Decis de": "Decided by",
  "Recalculează din valorile curente ale Activului/Amenințării/Hazardului":
    "Recalculate from the current Asset/Threat/Hazard values",

  // ── Tratamente (tab) ─────────────────────────────────────────────────
  "+ Tratament nou": "+ New treatment",
  "Interval de timp": "Time frame",
  "ex: 3 luni": "e.g. 3 months",
  "Acțiune": "Action",
  "Riscuri tratate": "Risks addressed",
  "Tratament": "Treatment",
  "Interval": "Timeframe",
  "Riscuri legate": "Linked risks",

  // ── Controale (tab) ──────────────────────────────────────────────────
  "Găsit": "Found",
  "Parțial": "Partial",
  "Negăsit": "Not found",
  "Politică": "Policy",
  "Procedură": "Procedure",
  "Altul": "Other",
  "Registrul de controale + gap-analysis: pentru fiecare control așteptat, marchează dacă a fost găsit documentat și evaluează adecvarea lui (Politică / Implementare / Asigurare), la fel ca la Registrul de risc.":
    "Control register + gap analysis: for each expected control, mark whether it was found documented and rate its adequacy (Policy / Implementation / Assurance), just like in the Risk register.",
  "+ Control nou": "+ New control",
  "Tip": "Type",
  "Status (gap-analysis)": "Status (gap analysis)",
  "Document / referință": "Document / reference",
  "Custode": "Custodian",
  "Ultima revizuire": "Last review",
  "ex: 2025": "e.g. 2025",
  "Scor de adecvare (1–5 fiecare)": "Adequacy score (1–5 each)",
  "Asigurare": "Assurance",
  "Control": "Control",
  "Status": "Status",
  "Document": "Document",
  "Adecvare (control)": "Adequacy",
  "Niciun control încă.": "No controls yet.",

  // ── Bow-Tie (tab) ────────────────────────────────────────────────────
  "Hazard → bariere pre-eveniment → eveniment → bariere post-eveniment → consecințe. Câte o linie pe bară/factor/consecință.":
    "Hazard → pre-event barriers → event → post-event barriers → consequences. One line per barrier/factor/consequence.",
  "+ Bow-tie nou": "+ New bow-tie",
  "Hazard (denumire)": "Hazard (name)",
  "Legătură cu Hazard existent (opțional)": "Link to existing Hazard (optional)",
  "Activ vizat (opțional)": "Targeted asset (optional)",
  "Amenințare (opțional)": "Threat (optional)",
  "Vulnerabilități": "Vulnerabilities",
  "Bariere pre-eveniment (o linie / barieră)": "Pre-event barriers (one line / barrier)",
  "Factori de escaladare (pre)": "Escalation factors (pre)",
  "Eveniment": "Event",
  "Bariere post-eveniment": "Post-event barriers",
  "Factori de escaladare (post)": "Escalation factors (post)",
  "Consecințe (o linie / consecință)": "Consequences (one line / consequence)",
  "Niciun bow-tie încă.": "No bow-ties yet.",
  "Bariere pre-eveniment": "Pre-event barriers",
  "Consecințe": "Consequences",

  // ── Cadrane de amenințare (tab) ──────────────────────────────────────
  "Calculate automat din valorile deja introduse la Amenințări și Active — nu alimentează Registrul de risc, sunt un instrument de prioritizare strategică (foaia ThreatQuadrants din xlsx).":
    "Automatically calculated from the values already entered for Threats and Assets — this does not feed into the Risk register, it's a strategic prioritization tool (ThreatQuadrants sheet from the xlsx).",
  "Capabilitate →": "Capability →",
  "Intenție →": "Intent →",
  "Oportunitate →": "Opportunity →",
  "Atractivitate →": "Attractiveness →",
  "Dezorganizat": "Disorganized",
  "Menține monitorizare de rutină și protecția de bază.": "Maintain routine monitoring and basic protection.",
  "Critic": "Critical",
  "Concentrează măsurile de protecție aici. Monitorizare continuă.": "Focus protective measures here. Continuous monitoring.",
  "Iritant": "Nuisance",
  "Gestionat prin proceduri de rutină.": "Managed through routine procedures.",
  "Nefocalizat": "Unfocused",
  "Impact major posibil dacă profilul țintei se schimbă. Monitorizare regulată.":
    "Major impact possible if the target's profile changes. Regular monitoring.",
  "Caută oportunități și puncte slabe proaspete. Vizionare liberă, scurtă.":
    "Looks for fresh opportunities and weaknesses. Brief, casual observation.",
  "Țintă primară": "Primary target",
  "Supraveghere pentru evaluarea vulnerabilităților, plan de atac.": "Surveillance to assess vulnerabilities, attack planning.",
  "Evitare": "Avoidance",
  "Atenție scăzută. Bypass fără pierderi.": "Low attention. Bypass without loss.",
  "Secundar": "Secondary",
  "Monitorizează schimbările de adecvare.": "Monitor changes in suitability.",
  "Perspectiva apărătorului asupra amenințării": "The defender's perspective on the threat",
  "Perspectiva amenințării asupra țintei": "The threat's perspective on the target",

  // ── Plan de lucru (tab) ──────────────────────────────────────────────
  "1. Stabilirea termenilor de referință": "1. Establishing the terms of reference",
  "2. Stabilirea contextului": "2. Establishing the context",
  "3. Identificarea riscurilor": "3. Risk identification",
  "4. Analiza riscurilor": "4. Risk analysis",
  "5. Evaluarea riscurilor": "5. Risk evaluation",
  "6. Acceptarea sau refuzul": "6. Acceptance or refusal",
  "7. Tratarea riscurilor": "7. Risk treatment",
  "8. Monitorizarea și revizuirea": "8. Monitoring and review",
  "9. Îmbunătățirea continuă": "9. Continuous improvement",
  "10. Revizuirea procesului": "10. Process review",
  "+ Sarcină nouă": "+ New task",
  "Fază": "Phase",
  "Sarcină": "Task",
  "Responsabil": "Owner",
  "Început": "Start",
  "Sfârșit": "End",
  "Progres (%)": "Progress (%)",
  "Progres": "Progress",
  "Nicio sarcină încă.": "No tasks yet.",

  // ── Raport (etapele SRM Process — ghid + cerințe) ────────────────────
  "Identificarea existenței unei politici de management al riscului, linii directoare, toleranță la risc etc.":
    "Identify whether a risk management policy exists, guidelines, risk tolerance, etc.",
  "Politica de management al riscului existentă (referință document, dacă există)":
    "Existing risk management policy (document reference, if any)",
  "Standardele/liniile directoare aplicate (ex. ISO 31000, TSRA)": "Applied standards/guidelines (e.g. ISO 31000, TSRA)",
  "Toleranța / apetitul la risc al organizației": "The organization's risk tolerance / appetite",
  "Scopul și limitele evaluării — ce e inclus, ce e exclus": "The scope and boundaries of the assessment — what's included, what's excluded",
  "Sponsorul intern și părțile responsabile de evaluare": "The internal sponsor and the parties responsible for the assessment",

  "Context strategic, organizațional și de management al riscului. Scopurile, obiectivele, mediul, valorile (ce avem și de ce le avem).":
    "Strategic, organizational, and risk management context. Goals, objectives, environment, values (what we have and why we have it).",
  "Contextul strategic și organizațional (mediu, obiective de business)": "The strategic and organizational context (environment, business objectives)",
  "Valorile/activele protejate și motivul pentru care sunt protejate": "The values/assets protected and why they are protected",
  "Părțile interesate interne și externe relevante": "Relevant internal and external stakeholders",
  "Criteriile de risc — cum se definesc probabilitatea și consecința pentru această evaluare":
    "Risk criteria — how likelihood and consequence are defined for this assessment",

  "Identificați vulnerabilități și amenințări. Ce se poate întâmpla și cum? Brainstorming, trecerea în revistă a incidentelor din trecut, ședințe, discuții etc.":
    "Identify vulnerabilities and threats. What could happen and how? Brainstorming, reviewing past incidents, meetings, discussions, etc.",
  "Toate activele relevante, cu criticitate și oportunitate evaluate (tab Active)": "All relevant assets, with criticality and opportunity rated (Assets tab)",
  "Toate amenințările relevante, cu capabilitate și intenție evaluate (tab Amenințări)": "All relevant threats, with capability and intent rated (Threats tab)",
  "Toate hazardurile/vulnerabilitățile, cu eficacitatea controalelor existente (tab Hazarduri)":
    "All hazards/vulnerabilities, with the effectiveness of existing controls (Hazards tab)",
  "Poziționarea în cadranele de amenințare — perspectiva apărătorului și a amenințării":
    "Positioning in the threat quadrants — the defender's and the threat's perspective",

  "Evaluați controalele existente, probabilitatea și consecințele apariției riscurilor.":
    "Assess existing controls, and the likelihood and consequences of the risks occurring.",
  "Probabilitatea de apariție — medie(indice amenințare, oportunitate activ)": "Likelihood of occurrence — average(threat index, asset opportunity)",
  "Consecința — medie(eficacitate hazard, criticitate activ)": "Consequence — average(hazard effectiveness, asset criticality)",
  "Scorul de risc inerent și banda de risc rezultată (FS/S/M/R/FR)": "The inherent risk score and the resulting risk band (FS/S/M/R/FR)",

  "Comparați cu criteriile stabilite. Setați prioritizarea riscurilor.": "Compare against the established criteria. Set the prioritization of risks.",
  "Compararea riscurilor identificate cu criteriile stabilite la etapa 2": "Comparing the identified risks against the criteria set in stage 2",
  "Distribuția riscurilor pe benzi (portofoliul de risc)": "The distribution of risks across bands (the risk portfolio)",
  "Prioritizarea riscurilor pentru etapa de tratare": "Prioritization of risks for the treatment stage",

  "Acceptați sau respingeți riscurile existente în conformitate cu toleranța/apetitul la risc stabilit și asumat de compania auditată.":
    "Accept or reject the existing risks in line with the risk tolerance/appetite set and accepted by the audited company.",
  "O decizie explicită pentru fiecare risc — acceptat sau respins": "An explicit decision for each risk — accepted or rejected",
  "Justificarea deciziei față de toleranța la risc stabilită la etapa 1": "Justification of the decision against the risk tolerance set in stage 1",
  "Persoana/funcția care a luat decizia": "The person/role who made the decision",

  "Implementați planul de tratament și raportați progresul ca parte a funcției obișnuite de raportare.":
    "Implement the treatment plan and report progress as part of the regular reporting function.",
  "Opțiunile de tratare alese pentru riscurile respinse (evitare, transfer, reducere, asumare)":
    "The treatment options chosen for rejected risks (avoidance, transfer, reduction, acceptance)",
  "Planul de tratamente, cu acțiuni și interval de timp": "The treatment plan, with actions and timeframe",
  "Registrul de controale — ce e găsit/parțial/negăsit față de ce era așteptat": "The control register — what is found/partial/not found against what was expected",
  "Diagramele bow-tie pentru scenariile critice (hazard → bariere → eveniment → consecințe)":
    "The bow-tie diagrams for critical scenarios (hazard → barriers → event → consequences)",

  "Monitorizați, raportați și actualizați planul de reglementare a riscurilor și de tratament în mod continuu.":
    "Continuously monitor, report on, and update the risk regulation and treatment plan.",
  "Planul de implementare a tratamentelor (fazele angajamentului, responsabili, termene)":
    "The treatment implementation plan (engagement phases, owners, deadlines)",
  "Progresul acțiunilor de tratare": "Progress of the treatment actions",
  "Data următoarei revizuiri periodice programate": "The date of the next scheduled periodic review",

  "Îmbunătățiți și dezvoltați sistemele și instrumentele de securitate existente.": "Improve and develop existing security systems and tools.",
  "Lecțiile învățate din acest ciclu de evaluare": "Lessons learned from this assessment cycle",
  "Îmbunătățirile aduse instrumentelor/sistemelor de securitate existente": "Improvements made to existing security tools/systems",
  "Recomandări de dus mai departe în ciclul următor": "Recommendations to carry forward into the next cycle",

  "Efectuați o evaluare anuală, reluând procesul de la pasul 1.": "Carry out an annual assessment, restarting the process from step 1.",
  "Confirmarea stării evaluării (ciornă / finalizată)": "Confirmation of the assessment status (draft / finalized)",
  "Programarea următoarei evaluări anuale, reluând procesul de la etapa 1": "Scheduling the next annual assessment, restarting the process from stage 1",

  // ── Raport — chrome ───────────────────────────────────────────────────
  "Se salvează…": "Saving…",
  "Salvează raportul": "Save report",
  "Editează narațiunea": "Edit narrative",
  "Printează raportul": "Print report",
  "Această etapă trebuie să conțină:": "This stage must contain:",
  "Narațiunea analistului pentru această etapă…": "The analyst's narrative for this stage…",
  "Fără narațiune încă.": "No narrative yet.",

  // ── RaportDateEtapa — headings & empty states ────────────────────────
  "Active identificate": "Assets identified",
  "Amenințări identificate": "Threats identified",
  "Hazarduri identificate": "Hazards identified",
  "Analiza amenințărilor (capabilitate × intenție)": "Threat analysis (capability × intent)",
  "Nicio amenințare încă.": "No threats yet.",
  "Analiza vulnerabilităților — active (oportunitate × criticitate)": "Vulnerability analysis — assets (opportunity × criticality)",
  "Niciun activ încă.": "No assets yet.",
  "Analiza vulnerabilităților — hazarduri (eficacitatea controalelor existente)": "Vulnerability analysis — hazards (effectiveness of existing controls)",
  "Niciun hazard încă.": "No hazards yet.",
  "Cadrane de poziționare (ThreatQuadrants)": "Positioning quadrants (ThreatQuadrants)",
  "Scor risc inerent": "Inherent risk score",
  "Niciun rând de risc încă.": "No risk rows yet.",
  "Tratamente planificate": "Planned treatments",
  "Diagrame Bow-Tie": "Bow-Tie diagrams",
  "Niciun tratament încă.": "No treatments yet.",
  "Registrul de controale (gap-analysis)": "Control register (gap analysis)",
  "Următoarea revizuire periodică": "Next periodic review",
  " — restantă": " — overdue",
  "Fără dată de revizuire periodică stabilită.": "No periodic review date set.",
  "Nicio sarcină de plan încă.": "No plan tasks yet.",
  "Stare evaluare": "Assessment status",
  "Data evaluării": "Assessment date",

  // ── EASI (Estimate of Adversary Sequence Interruption) ───────────────
  "Analiză EASI": "EASI analysis",
  "Modelează traseul cronologic al unui adversar (barieră cu barieră) și calculează Probabilitatea de Întrerupere — compară timpul rămas adversarului cu timpul forței de răspuns. Poți adăuga mai multe trasee per scenariu; sistemul e la fel de sigur ca cel mai slab traseu.":
    "Models an adversary's chronological path (barrier by barrier) and calculates the Probability of Interruption — comparing the adversary's remaining time with the response force's time. You can add several paths per scenario; the system is only as safe as the weakest path.",
  "+ Scenariu EASI nou": "+ New EASI scenario",
  "ex: Sustragere seif birou": "e.g. Office safe theft",
  "Denumire scenariu": "Scenario name",
  "Bun protejat (Activ, opțional)": "Protected asset (optional)",
  "Perimetru": "Perimeter",
  "Strategia adversarului": "Adversary strategy",
  "Forța de răspuns (comună tuturor traseelor scenariului)": "Response force (shared by all paths in the scenario)",
  "P. comunicare alarmă (0-1)": "P. alarm communication (0-1)",
  "Timp răspuns mediu (secunde)": "Mean response time (seconds)",
  "Deviație standard (secunde)": "Standard deviation (seconds)",
  "Niciun scenariu EASI încă.": "No EASI scenario yet.",
  "Bun protejat": "Protected asset",
  "medie": "mean",
  "Răspuns": "Response",
  "comunicare alarmă": "alarm communication",
  "Șterge scenariu": "Delete scenario",
  "+ Traseu nou": "+ New path",
  "ex: Traseu principal / Traseu după tratament": "e.g. Main path / Path after treatment",
  "Denumire traseu": "Path name",
  "Adaugă": "Add",
  "Timp total adversar": "Total adversary time",
  "PI traseu": "Path PI",
  "Șterge traseu": "Delete path",
  "Pas": "Step",
  "Element/catalog": "Element/catalog",
  "P det. tehnică": "P technical det.",
  "P validare op.": "P operator validation",
  "Timp mediu": "Mean time",
  "Deviație": "Deviation",
  "Detecție": "Detection",
  "P primă detecție": "P first detection",
  "Scor Z": "Z score",
  "P răspuns la timp": "P response in time",
  "Contribuție": "Contribution",
  "Detecție la finalul pasului — timpul propriu nu intră în fereastra de răspuns": "Detection at the end of the step — its own time does not count toward the response window",
  "Detecție la începutul pasului": "Detection at the start of the step",
  "final": "end",
  "început": "start",
  "+ Pas nou": "+ New step",
  "Descriere pas": "Step description",
  "Hazard asociat (opțional)": "Associated hazard (optional)",
  "Tratament asociat (opțional)": "Associated treatment (optional)",
  "P. detecție tehnică (0-1)": "P. technical detection (0-1)",
  "P. validare operator (0-1)": "P. operator validation (0-1)",
  "Timp mediu (secunde)": "Mean time (seconds)",
  "Detecție la finalul pasului": "Detection at the end of the step",
  "da (ex.: geam spart abia la impact final)": "yes (e.g. glass breaks only on final impact)",
  "Analiză EASI — Probabilitate de Întrerupere": "EASI analysis — Probability of Interruption",
  "Scenariu": "Scenario",
  "Trasee": "Paths",
  "Analiză EASI — Probabilitatea de Întrerupere pe traseul adversarului": "EASI analysis — Probability of Interruption on the adversary's path",
  "Bază (stare curentă)": "Baseline (current state)",
  "După tratamente (propus)": "After treatments (proposed)",
  "Traseu": "Path",
  "PI bază": "Baseline PI",
  "PI bază (sistem)": "Baseline PI (system)",
  "Diagramă traseu — stare curentă (toate căile posibile)": "Path diagram — current state (all possible paths)",
  "Diagramă traseu — după tratamente (toate căile posibile)": "Path diagram — after treatments (all possible paths)",
  "Fără zonă alocată": "No zone assigned",
  "PI": "PI",
  "PI după tratamente": "PI after treatments",
  "Delta": "Delta",
  "puncte": "points",
  "PI sistem — bază": "System PI — baseline",
  "PI sistem — după tratamente": "System PI — after treatments",
  "Pentru bunurile critice: analiza EASI (Probabilitate de Întrerupere) ca alternativă cuantificată la timpul de detecție vs. timpul de răspuns": "For critical assets: EASI analysis (Probability of Interruption) as a quantified alternative comparing detection time vs. response time",
  "Analiza EASI — Probabilitatea de Întrerupere înainte/după tratamentele propuse, pe traseele adversarului": "EASI analysis — Probability of Interruption before/after the proposed treatments, on the adversary's paths",
  "Cronologie": "Timeline",
  "Tabel detaliat": "Detailed table",
  "contribuție": "contribution",
  "Flux proces": "Process flow",
  "Progres proces": "Process progress",
  "Etapa curentă": "Current stage",
  "Toate etapele SRM Process sunt complete pentru această evaluare.": "All SRM Process stages are complete for this assessment.",
  "etape complete": "stages complete",
  "clic pe o stație pentru a merge direct la ea": "click a station to jump straight to it",
  "Etapă completă": "Stage complete",
  "Etapă viitoare": "Upcoming stage",

  // ── Ghid metodologie ──────────────────────────────────────────────────
  "← Înapoi": "← Back",
  "Portat din foile": "Ported from the",
  "și": "and",
  "ale TSRA_2024.xlsx. Grila de mai jos e un cadru calitativ paralel al aceleiași metodologii (Probabilitate × 6 categorii de consecință → VL/L/M/H/VH) — nu alimentează calculul din Registrul de risc. Banda afișată acolo (FS/S/M/R/FR) vine din suma Probabilitate + Consecință.":
    "sheets of TSRA_2024.xlsx. The grid below is a parallel qualitative framework of the same methodology (Probability × 6 consequence categories → VL/L/M/H/VH) — it does not feed the calculation in the Risk register. The band shown there (FS/S/M/R/FR) comes from the sum of Probability + Consequence.",
  "Matricea de risc": "Risk matrix",
  "Probabilitate ↓ / Severitate →": "Probability ↓ / Severity →",
  "Categorii de consecință": "Consequence categories",
  "Scalele de evaluare (1–5)": "Rating scales (1–5)",
  "Aceleași scale folosite la completarea Activelor, Amenințărilor și Hazardurilor.":
    "The same scales used when filling in Assets, Threats, and Hazards.",

  // ── scales.js — RISK_MATRIX ───────────────────────────────────────────
  "Nesemnificativ": "Insignificant",
  "Minor": "Minor",
  "Moderat": "Moderate",
  "Major": "Major",
  "Sever": "Severe",
  "Rar": "Rare",
  "Poate apărea doar în circumstanțe excepționale": "May occur only in exceptional circumstances",
  "Improbabil": "Unlikely",
  "Ar putea apărea la un moment dat": "Could occur at some time",
  "Posibil": "Possible",
  "S-ar putea întâmpla la un moment dat": "Might occur at some time",
  "Probabil": "Likely",
  "Se va întâmpla probabil în majoritatea circumstanțelor": "Will probably occur in most circumstances",
  "Aproape cert": "Almost certain",
  "Este de așteptat să se întâmple în majoritatea circumstanțelor": "Is expected to occur in most circumstances",

  "Persoane": "People",
  "Impact minor asupra competențelor. Vătămare minoră sau prim ajutor.": "Minor impact on capability. Minor injury or first aid.",
  "Impact minor asupra capacității. Vătămare ce necesită tratament medical.": "Minor impact on capacity. Injury requiring medical treatment.",
  "Indisponibilitatea competențelor de bază afectează serviciile. Vătămare majoră / spitalizare.":
    "Unavailability of core skills affects services. Major injury / hospitalization.",
  "Indisponibilitatea competențelor sau personalului critic. Deces unic și/sau vătămări majore multiple.":
    "Unavailability of critical skills or personnel. Single death and/or multiple major injuries.",
  "Indisponibilitate prelungită a competențelor critice. Decese multiple.": "Prolonged unavailability of critical skills. Multiple deaths.",

  "Informație": "Information",
  "Compromiterea unor informații deja disponibile public.": "Compromise of information already publicly available.",
  "Compromitere minoră a informațiilor sensibile la nivel intern sau de subunitate.":
    "Minor compromise of sensitive information at internal or sub-unit level.",
  "Compromiterea informațiilor sensibile pentru operațiunile organizației.": "Compromise of information sensitive to the organization's operations.",
  "Compromiterea informațiilor sensibile pentru interesele organizației.": "Compromise of information sensitive to the organization's interests.",
  "Compromiterea informațiilor cu impact semnificativ și de durată.": "Compromise of information with significant, lasting impact.",

  "Proprietate & Echipamente": "Property & Equipment",
  "Deteriorare minoră sau vandalism asupra unui activ.": "Minor damage or vandalism to an asset.",
  "Deteriorare minoră sau pierderea a sub 5% din active.": "Minor damage or loss of under 5% of assets.",
  "Deteriorare sau pierderea a sub 20% din active.": "Damage or loss of under 20% of assets.",
  "Deteriorare extinsă sau pierderea a sub 50% din active.": "Extensive damage or loss of under 50% of assets.",
  "Distrugerea sau pierderea completă a peste 50% din active.": "Destruction or complete loss of over 50% of assets.",

  "Reputație": "Reputation",
  "Mențiune locală, uitată rapid. Fără impact asupra activității.": "Local mention, quickly forgotten. No impact on operations.",
  "Atenție din partea conducerii sau auditului intern, pentru a preveni escaladarea. Interes media local, pe termen scurt.":
    "Attention from management or internal audit to prevent escalation. Short-term local media interest.",
  "Îngrijorare națională persistentă. Necesită atenția unor agenții externe. Impact de durată asupra imaginii.":
    "Persistent national concern. Requires attention from external agencies. Lasting impact on image.",
  "Atenție publică, politică și media națională intensă și persistentă. Operațiuni majore sever restricționate.":
    "Intense and persistent public, political, and national media attention. Major operations severely restricted.",
  "Îngrijorare internațională, anchetă guvernamentală sau atenție media națională/internațională susținută.":
    "International concern, government inquiry, or sustained national/international media attention.",

  "Financiar": "Financial",
  "≈1% din bugetul anual al proiectului/organizației.": "≈1% of the project's/organization's annual budget.",
  "2–5% din bugetul anual al proiectului/organizației.": "2–5% of the project's/organization's annual budget.",
  "5–10% din bugetul anual al proiectului/organizației.": "5–10% of the project's/organization's annual budget.",
  "peste 10% din bugetul anual al proiectului/organizației.": "over 10% of the project's/organization's annual budget.",
  "peste 30% din bugetul anual al proiectului/organizației.": "over 30% of the project's/organization's annual budget.",

  "Impact minim asupra operațiunilor non-esențiale, gestionabil prin activitatea curentă.":
    "Minimal impact on non-essential operations, manageable through current activity.",
  "Impact asupra unor zone de activitate (întârzieri, calitate), gestionabil la nivel operațional.":
    "Impact on some operational areas (delays, quality), manageable at operational level.",
  "Performanța scade sub țintă; poate necesita revizuirea modului de operare.":
    "Performance drops below target; may require reviewing how operations are run.",
  "Blocarea unor activități-cheie; scade performanța (întârzieri, pierderi de venit, clienți nemulțumiți); supraviețuirea proiectului este amenințată.":
    "Blockage of key activities; performance drops (delays, revenue loss, dissatisfied customers); project survival is threatened.",
  "Eșec(uri) critice care împiedică activitățile de bază; amenință supraviețuirea proiectului sau a organizației.":
    "Critical failure(s) preventing core activities; threatens the survival of the project or organization.",

  "Acțiune imediată necesară din partea conducerii de vârf: planificare detaliată, alocare de resurse, monitorizare permanentă.":
    "Immediate action required from top management: detailed planning, resource allocation, continuous monitoring.",
  "Risc ridicat — necesită atenția managementului de vârf.": "High risk — requires the attention of top management.",
  "Responsabilitatea managementului trebuie precizată explicit.": "Management responsibility must be explicitly specified.",
  "Se monitorizează și gestionează prin proceduri de rutină.": "Monitored and managed through routine procedures.",
  "Se gestionează prin proceduri de rutină.": "Managed through routine procedures.",

  // ── scales.js — SCALES (13 scale descriptive, 5 nivele fiecare) ─────
  "Finanțare și/sau resurse aproape inexistente (financiar, uman, profesional)": "Funding and/or resources almost non-existent (financial, human, professional)",
  "Finanțare și/sau resurse puține (financiar, uman, profesional)": "Little funding and/or resources (financial, human, professional)",
  "Nivel mediu de finanțare și/sau resurse (financiar, uman, profesional)": "Medium level of funding and/or resources (financial, human, professional)",
  "Nivel ridicat de finanțare și/sau resurse (financiar, uman, profesional)": "High level of funding and/or resources (financial, human, professional)",
  "Nivel foarte ridicat de finanțare și/sau resurse (financiar, uman, profesional)": "Very high level of funding and/or resources (financial, human, professional)",

  "Lipsă de capabilitate, cunoștințe și instruire": "Lack of capability, knowledge, and training",
  "Capabilitate, cunoștințe și instruire limitate": "Limited capability, knowledge, and training",
  "Capabilitate, cunoștințe și instruire moderate": "Moderate capability, knowledge, and training",
  "Capabilitate, cunoștințe și instruire ridicate": "High capability, knowledge, and training",
  "Capabilitate, cunoștințe și instruire extrem de profesionale": "Extremely professional capability, knowledge, and training",

  "Absența dorinței și/sau a scopului": "Absence of desire and/or purpose",
  "Dorință de a obține rezultate prin mijloace pașnice": "Desire to achieve results through peaceful means",
  "Foarte motivat, dar cu o flexibilitate însemnată privind compromisul sau metoda": "Highly motivated, but with significant flexibility on compromise or method",
  "Foarte motivat, dar cu o flexibilitate redusă privind compromisul sau metoda": "Highly motivated, but with reduced flexibility on compromise or method",
  "Foarte motivat (ex.: extremist), fără flexibilitate privind compromisul sau metoda": "Highly motivated (e.g. extremist), with no flexibility on compromise or method",

  "Nu consideră că are capacitatea de a iniția și încheia o acțiune": "Does not believe they have the capacity to initiate and complete an action",
  "Consideră că are capacități limitate de a iniția și încheia o acțiune": "Believes they have limited capacity to initiate and complete an action",
  "Consideră că are capacități medii de a iniția și încheia o acțiune": "Believes they have moderate capacity to initiate and complete an action",
  "Consideră că are capacități ridicate de a iniția și încheia o acțiune": "Believes they have high capacity to initiate and complete an action",
  "Perfect convins că are capacități maxime pentru a iniția și încheia cu succes o acțiune": "Fully convinced they have maximum capacity to successfully initiate and complete an action",

  "Atractivitate foarte redusă": "Very low attractiveness",
  "Atractivitate limitată": "Limited attractiveness",
  "Atractivitate moderată": "Moderate attractiveness",
  "Atractivitate ridicată": "High attractiveness",
  "Atractivitate extremă": "Extreme attractiveness",

  "Expunere redusă (de câteva ori pe an)": "Low exposure (a few times a year)",
  "Expunere cu frecvență lunară": "Monthly exposure",
  "Expunere cu frecvență săptămânală": "Weekly exposure",
  "Expunere zilnică, în parametrii unui program de lucru standard": "Daily exposure, within a standard working schedule",
  "Expunere totală, 24/7": "Total exposure, 24/7",

  "Acces foarte limitat, aproape inexistent": "Very limited access, almost non-existent",
  "Acces limitat, condiționat": "Limited, conditional access",
  "Acces moderat": "Moderate access",
  "Ușor de accesat în aproape orice condiție": "Easy to access under almost any condition",
  "Acces public": "Public access",

  "Greu de aplicat ca amenințare de securitate": "Difficult to apply as a security threat",
  "Poate fi modificat sau folosit cu un anumit efect": "Can be modified or used with some effect",
  "Eficacitate moderată ca amenințare de securitate": "Moderate effectiveness as a security threat",
  "Potrivit pentru utilizare ca amenințare de securitate": "Suitable for use as a security threat",
  "Foarte eficient în exploatarea vulnerabilităților de securitate": "Highly effective at exploiting security vulnerabilities",

  "Greu până la imposibil de obținut": "Difficult to impossible to obtain",
  "Capacitate redusă de a fi obținut": "Low likelihood of being obtained",
  "Disponibilitate limitată, dar poate fi obținut de actori calificați": "Limited availability, but can be obtained by qualified actors",
  "Poate fi obținut în majoritatea circumstanțelor": "Can be obtained in most circumstances",
  "Disponibil și ușor de obținut": "Available and easy to obtain",

  "Implementarea este complicată și necesită o cantitate mare de resurse": "Implementation is complicated and requires a large amount of resources",
  "Implementarea este o provocare și necesită expertiză de specialitate": "Implementation is challenging and requires specialist expertise",
  "Moderat, ușor de implementat": "Moderate, easy to implement",
  "Poate fi implementat relativ ușor împotriva vulnerabilităților": "Can be implemented relatively easily against vulnerabilities",
  "Extrem de ușor de implementat": "Extremely easy to implement",

  "Se va recupera destul de ușor în urma majorității evenimentelor": "Will recover fairly easily from most events",
  "Se poate recupera de la majoritatea evenimentelor": "Can recover from most events",
  "Se poate recupera de la majoritatea evenimentelor, dar poate dura mult timp": "Can recover from most events, but it may take a long time",
  "Se poate recupera, dar cu dificultate și pe o perioadă lungă de timp": "Can recover, but with difficulty and over a long period",
  "Puțină rezistență sau abilitate de a se recupera de la evenimente ieșite din comun": "Little resilience or ability to recover from unusual events",

  "Activ de scurtă durată sau cu cerințe de funcționare necritice": "Short-lived asset or with non-critical operating requirements",
  "Cerințe minime privind durata de viață și/sau cerințe flexibile de funcționare": "Minimal lifespan requirements and/or flexible operating requirements",
  "Cerințe moderate de durată de viață și/sau de funcționare": "Moderate lifespan and/or operating requirements",
  "Durata de funcționare esențială în majoritatea circumstanțelor și/sau așteptări prelungite pe durata de viață":
    "Operating uptime essential in most circumstances and/or extended lifespan expectations",
  "Capacitate operațională necesară 24/7; esențial și/sau cerință de viață extinsă": "24/7 operational capacity required; essential and/or extended lifespan requirement",

  "Minimă sau deloc dependență de acest activ pentru atingerea obiectivelor organizaționale": "Minimal or no dependency on this asset to achieve organizational objectives",
  "Dependență minimă de acest activ": "Minimal dependency on this asset",
  "Nivel moderat de dependență în majoritatea circumstanțelor": "Moderate level of dependency in most circumstances",
  "Activul este o dependență de bază a organizației sau a proiectului": "The asset is a core dependency of the organization or project",
  "Dependență critică de acest activ": "Critical dependency on this asset",

  // Costuri (cuantificarea tratamentelor)
  "Cost total estimat": "Total estimated cost",
  "investiție inițială (unic)": "initial investment (one-time)",
  "cost recurent, anualizat": "recurring cost, annualized",
  "Sisteme tehnice": "Technical systems",
  "Bariere fizice": "Physical barriers",
  "Resursă umană": "Human resource",
  "Proceduri / consultanță": "Procedures / consulting",
  "Unic": "One-time",
  "Lunar": "Monthly",
  "Anual": "Annual",
  "Recurență": "Recurrence",
  "Cantitate": "Quantity",
  "Total": "Total",
  "+ Linie de cost": "+ New cost line",
  "Valoare unitară": "Unit value",
  "Unitate de măsură": "Unit of measure",
  "ex: zile, persoane, lună": "e.g.: days, people, month",
  "Monedă": "Currency",
  "Notă / justificare": "Note / justification",
  "ex: 3 agenți × 8h × 15 USD/h × 365 zile": "e.g.: 3 guards × 8h × $15/h × 365 days",
  "ex: Escortă armată subcontractată": "e.g.: Subcontracted armed escort",
  "riscuri legate": "linked risks",
  "unic": "one-time",
  "an": "yr",
  "Cost": "Cost",

  // Evidențe (probe colectate per etapă)
  "Evidențe colectate": "Evidence collected",
  "Data colectării": "Date collected",
  "Colectat de": "Collected by",
  "Link": "Link",
  "deschide": "open",
  "+ Evidență nouă": "+ New evidence",
  "Email": "Email",
  "Fotografie": "Photograph",
  "Raport terț": "Third-party report",
  "ex: Procedură de escortă transporturi, semnată": "e.g.: Signed transport-escort procedure",
  "Link extern (email, SharePoint, Drive etc.)": "External link (email, SharePoint, Drive, etc.)",
  "Fiecare tratament, cu acțiunea planificată și structura de cost pe linii — cum se ajunge la totalul fiecărei valori.":
    "Each treatment, with its planned action and line-by-line cost breakdown — how each total is reached.",
  "Linie de cost": "Cost line",
  "Cantitate × valoare unitară": "Quantity × unit value",
  "Total linie": "Line total",
  "Fără cost cuantificat.": "No cost quantified.",
  "— selectează un tip de tratament standard —": "— select a standard treatment type —",
  "Salvează acest tip și în catalog, pentru refolosire ulterioară": "Also save this type in the catalog, for future reuse",
  "Zonă": "Zone",
  "Zonă / strat de protecție (opțional)": "Zone / protection layer (optional)",
  "ex: Zonă limitată, Clădire controlată, Incintă țintă": "e.g.: Limited zone, Controlled building, Target enclosure",

  // Supraveghere (Surveillance Opportunity Matrix)
  "Supraveghere": "Surveillance",
  "Evaluează cât de expus e fiecare subiect la supraveghere ostilă înainte de un atac (recunoaștere adversă) — contramăsuri vizuale/acustice/electronice, scor 1-5. Complementar Registrului de risc și analizei EASI, nu le înlocuiește și nu le alimentează.":
    "Assesses how exposed each subject is to hostile surveillance before an attack (adversary reconnaissance) — visual/acoustic/electronic countermeasures, 1-5 score. Complementary to the Risk register and EASI analysis, not a replacement or an input to them.",
  "Personal": "Personnel",
  "Zone": "Zones",
  "ex: Conducere executivă, Angajați, Contractori, Vizitatori": "e.g.: Executive management, Employees, Contractors, Visitors",
  "ex: Perimetru parcare, Recepție, Depozit extern": "e.g.: Parking perimeter, Reception, External warehouse",
  "Vizuală fixă": "Fixed visual",
  "Vizuală mobilă": "Mobile visual",
  "Acustică": "Acoustic",
  "Electronică": "Electronic",
  "Sisteme IT": "IT systems",
  "Interceptare info": "Info interception",
  "Scor": "Score",
  "+ Subiect nou": "+ New subject",
  "Niciun subiect încă.": "No subject yet.",
  "Matricea oportunităților de supraveghere (Surveillance Opportunity Matrix)": "Surveillance Opportunity Matrix",
  "Cât de bine protejat e fiecare subiect împotriva supravegherii ostile înainte de un atac — complementar registrului de risc, nu îl înlocuiește.":
    "How well protected each subject is against hostile surveillance before an attack — complementary to the risk register, not a replacement for it.",

  // Catalog personalizat — elemente de protecție (EASI)
  "Elementul nu e în listă? Adaugă-l în catalog": "Can't find the element? Add it to the catalog",
  "Cod (opțional)": "Code (optional)",
  "ex: RC7, SEIF-GR4": "e.g.: RC7, SAFE-GR4",
  "Tip element": "Element type",
  "Ușă / fereastră / oblon": "Door / window / shutter",
  "Seif": "Safe",
  "Geam": "Glazing",
  "Clasă (opțional)": "Class (optional)",
  "Timp rezistență (secunde)": "Resistance time (seconds)",
  "Sursă standard (opțional)": "Reference standard (optional)",
  "ex: EN 1627:2011, clasa RC7": "e.g.: EN 1627:2011, class RC7",
  "Comentarii (opțional)": "Comments (optional)",
  "Salvează în catalog și precompletează pasul": "Save to catalog and prefill the step",
  "Niciun risc în Registrul de risc încă — adaugă riscuri acolo ca să le poți lega de acest tratament.":
    "No risks in the Risk register yet — add risks there so you can link them to this treatment.",

  // Diagrame izometrice — straturi de protecție EASI + benzi de risc extrudate
  "Straturi de protecție — vedere de ansamblu": "Protection layers — overview",
  "Straturi de protecție izometrice, cu traseele adversarului suprapuse peste zone":
    "Isometric protection layers, with the adversary's routes overlaid on the zones",
  "Linie continuă = stare curentă, linie punctată = după tratamente. Culoarea traseului corespunde cu tabelele de mai jos.":
    "Solid line = current state, dotted line = after treatments. The route color matches the tables below.",
  "Distribuția riscurilor pe benzi de severitate, ca blocuri extrudate cu înălțime proporțională cu numărul de riscuri":
    "Risk distribution by severity band, as extruded blocks with height proportional to the number of risks",

  // Caracterizarea locației (tab Locație + secțiune Raport)
  "Locație": "Location",
  "Caracterizarea locației supuse evaluării": "Characterization of the assessed location",
  "Caracterizarea fizică a locației evaluate — căi de acces, structuri, utilități, materiale periculoase și zone de impact. Nu dublează Activele/Amenințările (au tab propriu) — se leagă de ele: o Structură poate fi asociată unui Activ, o Cale de acces unui pas din traseul EASI, iar o Zonă de impact poate precompleta zona pașilor EASI.":
    "Physical characterization of the assessed location — access routes, structures, utilities, hazardous materials, and impact zones. Doesn't duplicate Assets/Threats (they have their own tab) — it links to them: a Structure can be associated with an Asset, an Access route with an EASI path step, and an Impact zone can prefill the zone of EASI steps.",
  "Zone de impact": "Impact zones",
  "Căi de acces": "Access routes",
  "Structuri existente": "Existing structures",
  "Sisteme de utilități": "Utility systems",
  "Materiale periculoase stocate": "Hazardous materials stored",
  "Cale de acces": "Access route",
  "Structură existentă": "Existing structure",
  "Sistem de utilități": "Utility system",
  "Material periculos stocat": "Hazardous material stored",
  "Zonă de impact": "Impact zone",
  "Exterioară": "Exterior",
  "Interioară": "Interior",
  "Scăzut": "Low",
  "Mediu": "Medium",
  "Ridicat": "High",
  "Public": "Public",
  "Restricționat": "Restricted",
  "Punct de control": "Control point",
  "Trafic": "Traffic",
  "fără": "none",
  "da": "yes",
  "nu": "no",
  "punct unic de defecțiune": "single point of failure",
  "Are punct de control existent": "Has an existing control point",
  "Tip punct de control": "Control point type",
  "ex: gardă, badge, cod PIN": "e.g. guard, badge, PIN code",
  "Tip cale": "Route type",
  "Volum de trafic": "Traffic volume",
  "Tip structură": "Structure type",
  "ex: clădire principală, anexă, depozit": "e.g. main building, annex, warehouse",
  "Funcțiune": "Function",
  "Suprafață / etaje": "Area / floors",
  "Tip utilitate": "Utility type",
  "ex: comunicații, ventilație, gaz, electricitate": "e.g. communications, ventilation, gas, electricity",
  "Sursă": "Source",
  "internă / furnizor extern": "internal / external supplier",
  "Punct unic de defecțiune (single point of failure)": "Single point of failure",
  "Tip material": "Material type",
  "Cantitate / volum": "Quantity / volume",
  "Clasificare pericol": "Hazard classification",
  "ex: inflamabil, toxic, coroziv": "e.g. flammable, toxic, corrosive",
  "Nivel de clasificare": "Classification level",
  "Personal permis (opțional)": "Authorized personnel (optional)",
  "Zonă asociată (opțional)": "Associated zone (optional)",
  "ex: Perimetru exterior": "e.g. Outer perimeter",
  "Descriere / observații": "Description / notes",
  "Niciun element încă.": "No elements yet.",
  "Creează Hazard din acest element": "Create a Hazard from this element",
  "Salvează Hazard": "Save Hazard",
  "Structură (opțional — din tab-ul Locație)": "Structure (optional — from the Location tab)",
  "Nespecificată": "Unspecified",
  "Structură": "Structure",
  "…sau selectează dintr-o zonă înregistrată (precompletează câmpul de mai sus)": "…or pick a registered zone (prefills the field above)",
  "Alege o zonă din tab-ul Locație": "Pick a zone from the Location tab",
  "Cale de acces catalogată (opțional)": "Catalogued access route (optional)",
  "Clasificare": "Classification",
  "Personal permis": "Authorized personnel",
  "Punct unic de defecțiune": "Single point of failure",
  "Structura selectată nu aparține acestei evaluări.": "The selected structure doesn't belong to this evaluation.",
  "Calea de acces selectată nu aparține acestei evaluări.": "The selected access route doesn't belong to this evaluation.",
};

// ──────────────────────────────────────────────────────────────────────
// Helper de traducere — RO e mereu sursa de adevăr / fallback sigur.
// ──────────────────────────────────────────────────────────────────────
export function t(lang, roText) {
  if (lang === "en" && Object.prototype.hasOwnProperty.call(EN_STRINGS, roText)) {
    return EN_STRINGS[roText];
  }
  return roText;
}

// Formatare dată — ro-RO / en-GB (DD/MM/YYYY în ambele, ca să nu inducă în
// eroare cu formatul american MM/DD/YYYY într-o unealtă de risc).
export function fmtDate(lang, value) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString(lang === "en" ? "en-GB" : "ro-RO");
}

// ──────────────────────────────────────────────────────────────────────
// Context + hook
// ──────────────────────────────────────────────────────────────────────
const LangContext = createContext(null);

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem(LANG_KEY) === "en" ? "en" : "ro";
    } catch {
      return "ro";
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(LANG_KEY, lang);
    } catch {
      // localStorage indisponibil (mod privat etc.) — starea rămâne doar în memorie
    }
  }, [lang]);

  return <LangContext.Provider value={{ lang, setLang }}>{children}</LangContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang() trebuie folosit în interiorul <LangProvider>");
  return ctx;
}

// ──────────────────────────────────────────────────────────────────────
// Comutator RO / EN — text simplu, fără iconițe, turcoaz pe activ.
// ──────────────────────────────────────────────────────────────────────
export function LangSwitch({ className }) {
  const { lang, setLang } = useLang();
  return (
    <span className={`lang-switch${className ? ` ${className}` : ""}`}>
      <button
        type="button"
        className={lang === "ro" ? "lang-active" : "lang-inactive"}
        onClick={() => setLang("ro")}
        aria-pressed={lang === "ro"}
      >
        RO
      </button>
      <span className="lang-sep">/</span>
      <button
        type="button"
        className={lang === "en" ? "lang-active" : "lang-inactive"}
        onClick={() => setLang("en")}
        aria-pressed={lang === "en"}
      >
        EN
      </button>
    </span>
  );
}
